import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../utils/api';
import { toast } from 'sonner';
import { Lock, Key, Eye, EyeOff, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    token: searchParams.get('token') || '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'newPassword') {
      setPasswordStrength({
        length: value.length >= 8,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /[0-9]/.test(value),
      });
    }
  };

  const isPasswordValid = Object.values(passwordStrength).every(Boolean);
  const passwordsMatch = formData.newPassword === formData.confirmPassword && formData.confirmPassword !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.token) {
      toast.error('Please enter your reset token');
      return;
    }
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(formData.token, formData.newPassword);
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. Token may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-950 p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png" 
            alt="MBConstruction" 
            className="w-28 h-28 mx-auto mb-2 rounded-2xl object-contain bg-white p-3 shadow-lg"
          />
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {!success ? (
            <>
              {/* Header */}
              <div className="px-6 py-5 border-b border-slate-200">
                <h1 className="text-xl font-semibold text-slate-900">Reset Your Password</h1>
                <p className="text-sm text-slate-600 mt-1">
                  Enter the reset token from your email and create a new password
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* Reset Token */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Reset Token
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="token"
                      value={formData.token}
                      onChange={handleChange}
                      placeholder="Paste your reset token here"
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Check your email for the reset token
                  </p>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Enter new password"
                      className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  
                  {/* Password Requirements */}
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-slate-500 font-medium">Password must have:</p>
                    <div className="grid grid-cols-2 gap-1">
                      <div className={`flex items-center gap-1 text-xs ${passwordStrength.length ? 'text-green-600' : 'text-slate-400'}`}>
                        <CheckCircle className="w-3 h-3" />
                        <span>At least 8 characters</span>
                      </div>
                      <div className={`flex items-center gap-1 text-xs ${passwordStrength.uppercase ? 'text-green-600' : 'text-slate-400'}`}>
                        <CheckCircle className="w-3 h-3" />
                        <span>Uppercase letter</span>
                      </div>
                      <div className={`flex items-center gap-1 text-xs ${passwordStrength.lowercase ? 'text-green-600' : 'text-slate-400'}`}>
                        <CheckCircle className="w-3 h-3" />
                        <span>Lowercase letter</span>
                      </div>
                      <div className={`flex items-center gap-1 text-xs ${passwordStrength.number ? 'text-green-600' : 'text-slate-400'}`}>
                        <CheckCircle className="w-3 h-3" />
                        <span>Number</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm new password"
                      className={`w-full pl-10 pr-10 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formData.confirmPassword && !passwordsMatch 
                          ? 'border-red-300 bg-red-50' 
                          : passwordsMatch && formData.confirmPassword
                            ? 'border-green-300 bg-green-50'
                            : 'border-slate-200'
                      }`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {formData.confirmPassword && !passwordsMatch && (
                    <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !isPasswordValid || !passwordsMatch}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Password Reset Successful!</h2>
              <p className="text-slate-600 mb-6">
                Your password has been changed successfully. You can now login with your new password.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
              >
                Go to Login
              </button>
            </div>
          )}

          {/* Footer */}
          {!success && (
            <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-800"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
